import { useMatches, useRouter } from "@tanstack/react-router";
import { cn } from "tailwind-variants";
import { useGetUmbracoDeliveryApiV2LocalizationSuspense } from "#/_generated/umbraco/delivery-api/features/languages/languages.ts";
import type {
	IApiContentResponseModelBase,
	IApiContentResponseModelBaseCultures,
	LanguageDto,
	LanguageSelectorUmbBlockElementModel,
	LanguageSelectorUmbBlockSettingsElementModel,
	StartUmbPageContentResponseModel,
} from "#/_generated/umbraco/delivery-api/models";
import { tryGetButtonVariant } from "#/_packages/umbrella/features/button/utils";
import { tryGetIconSize } from "#/_packages/umbrella/features/icons/components/umb-sprite-icon.tsx";
import {
	type LanguageIconPosition,
	type LanguageItem,
	LanguageSelector,
	type LanguageTextLabelVariant,
} from "#/_packages/umbrella/features/language-selector/components/language-selector";
import useGetStartPage from "#/_packages/umbrella/integrations/umbraco/hooks/useGetStartPage.tsx";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { tryGetIconPosition } from "#/_packages/umbrella/integrations/umbraco/utils/global.ts";
import {
	tryGetAlignment,
	tryGetIntent,
	tryGetSize,
} from "#/_packages/umbrella/utils";

type LanguageSelectorBlockProps = BlockItemProps & {
	content: LanguageSelectorUmbBlockElementModel;
	settings: LanguageSelectorUmbBlockSettingsElementModel;
};

const languageTextLabelVariants = ["none", "abbreviation", "text"] as const;

function normalizePath(path: string | null | undefined) {
	if (!path) return "/";
	const normalized = path.startsWith("/") ? path : `/${path}`;
	return normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;
}

function toLanguageAbbreviation(culture: string) {
	const language = culture.split("-")[0] ?? culture;
	return language.charAt(0).toUpperCase() + language.slice(1).toLowerCase();
}

function tryGetLanguageTextLabelVariant(
	value?: string | null,
): LanguageTextLabelVariant {
	return languageTextLabelVariants.includes(value as LanguageTextLabelVariant)
		? (value as LanguageTextLabelVariant)
		: "text";
}

function getCurrentCulture(
	currentContent: IApiContentResponseModelBase | undefined,
	languages: readonly LanguageDto[],
) {
	if (!currentContent) {
		return languages.find((language) => language.isDefault)?.culture;
	}

	const currentPath = normalizePath(currentContent.route.path);
	const cultureEntry = Object.entries(currentContent.cultures).find(
		([, route]) => normalizePath(route.path) === currentPath,
	);

	return (
		cultureEntry?.[0] ??
		languages.find((language) => language.isDefault)?.culture ??
		languages[0]?.culture
	);
}

function mapLanguages(
	contentCultures: IApiContentResponseModelBaseCultures,
	languages: readonly LanguageDto[],
	currentCulture: string | undefined,
	startPage: StartUmbPageContentResponseModel | null,
): LanguageItem[] {
	const seen = new Set<string>();
	return languages.flatMap((language) => {
		const code = language.culture.toLowerCase();
		if (seen.has(code)) return [];
		seen.add(code);
		const href =
			[
				contentCultures[language.culture]?.path,
				startPage?.cultures[language.culture]?.path,
				language.url,
			].find((path) => path?.trim() && !path.trimStart().startsWith("#")) ?? "";

		return [
			{
				code: language.culture,
				label: language.name,
				abbreviation: toLanguageAbbreviation(language.culture),
				href,
				...(href ? {} : { isDisabled: true }),
				isActive:
					!!currentCulture &&
					language.culture.localeCompare(currentCulture, undefined, {
						sensitivity: "accent",
					}) === 0,
			},
		];
	});
}

export default function LanguageSelectorUmbBlock({
	className,
	content,
	settings,
}: LanguageSelectorBlockProps) {
	const isPreview = !!useRouter().options.context.previewBlock;
	return isPreview ? (
		<LanguageSelectorRenderer
			className={className}
			content={content}
			settings={settings}
			languages={[]}
			isPreview
		/>
	) : (
		<Default className={className} content={content} settings={settings} />
	);
}

function Default({ className, content, settings }: LanguageSelectorBlockProps) {
	const currentContent = useMatches({
		select: (matches) => {
			for (let index = matches.length - 1; index >= 0; index--) {
				const data = matches[index].loaderData as
					| { content?: IApiContentResponseModelBase }
					| undefined;
				if (data?.content) return data.content;
			}
			return undefined;
		},
	});
	const contentCultures = currentContent?.cultures;
	const { data } = useGetUmbracoDeliveryApiV2LocalizationSuspense();
	const startPage = useGetStartPage();
	const languagesData = data.data ?? [];
	const currentCulture = currentContent
		? getCurrentCulture(currentContent, languagesData)
		: undefined;

	const languages = contentCultures
		? mapLanguages(contentCultures, languagesData, currentCulture, startPage)
		: [];
	return (
		<LanguageSelectorRenderer
			className={className}
			content={content}
			settings={settings}
			languages={languages}
		/>
	);
}

function LanguageSelectorRenderer({
	className,
	content,
	settings,
	languages,
	isPreview = false,
}: LanguageSelectorBlockProps & {
	languages: readonly LanguageItem[];
	isPreview?: boolean;
}) {
	const alignmentSelfVertical = tryGetAlignment(
		settings.properties?.alignmentSelfVertical,
		"stretch",
	);
	const alignmentClass =
		alignmentSelfVertical === "stretch" ? "" : `self-${alignmentSelfVertical}`;

	return (
		<div
			id={settings.properties?.generatedId ?? content.id}
			className={cn(alignmentClass, className)}
		>
			<LanguageSelector
				languages={languages}
				isPreview={isPreview}
				textLabelVariant={tryGetLanguageTextLabelVariant(
					content.properties?.textLabelVariant,
				)}
				size={tryGetSize(content.properties?.size)}
				intentColor={tryGetIntent(content.properties?.intentColor)}
				typeVariant={tryGetButtonVariant(content.properties?.typeVariant)}
				icon={content.properties?.icon}
				iconPosition={
					tryGetIconPosition(
						content.properties?.iconPosition,
					) as LanguageIconPosition
				}
				iconSize={tryGetIconSize(content.properties?.size)}
			/>
		</div>
	);
}
